import { supabase } from './supabase';

export type Inquiry = {
  id: string;
  name: string;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  service?: string | null;
  budget?: string | null;
  message?: string | null;
  status: 'pending' | 'review' | 'completed' | string;
  created_at: string;
};

export type Project = {
  id: string;
  name: string;
  link?: string | null;
  description?: string | null;
  category?: string | null;
  tech?: string[] | null;
  image?: string | null;
  featured: boolean;
  created_at?: string;
};

const LOCAL_INQUIRIES_KEY = 'formacode_inquiries_cache';

export function normalizeProjectLink(url?: string | null): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed || trimmed === '#') return trimmed;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function getCachedInquiries(): Inquiry[] | null {
  try {
    const raw = localStorage.getItem(LOCAL_INQUIRIES_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return null;
}

function setCachedInquiries(inquiries: Inquiry[]) {
  try {
    localStorage.setItem(LOCAL_INQUIRIES_KEY, JSON.stringify(inquiries));
  } catch {}
}

function notifyInquiriesUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('inquiries_updated'));
  }
}

// 1. INQUIRIES API
export async function submitContactInquiry(formData: {
  name: string;
  company?: string;
  email: string;
  phone?: string;
  service?: string;
  budget?: string;
  message: string;
}): Promise<{ success: boolean; data?: Inquiry; error?: string }> {
  let savedInquiry: Inquiry = {
    id: 'inq_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7),
    name: formData.name.trim(),
    company: formData.company?.trim() || null,
    email: formData.email.trim(),
    phone: formData.phone?.trim() || null,
    service: formData.service?.trim() || null,
    budget: formData.budget?.trim() || null,
    message: formData.message.trim(),
    status: 'pending',
    created_at: new Date().toISOString(),
  };

  let backendSuccess = false;

  // 1. Try Express API Endpoint
  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.data) {
        savedInquiry = data.data;
        backendSuccess = true;
      }
    }
  } catch (err) {
    console.warn('API contact fetch notice:', err);
  }

  // 2. If backend was not reached, attempt Supabase fallback
  if (!backendSuccess) {
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .insert({
          name: savedInquiry.name,
          company: savedInquiry.company,
          email: savedInquiry.email,
          phone: savedInquiry.phone,
          service: savedInquiry.service,
          budget: savedInquiry.budget,
          message: savedInquiry.message,
          status: 'pending',
        })
        .select()
        .single();

      if (!error && data) {
        savedInquiry = data as Inquiry;
      }
    } catch (err) {
      console.warn('Supabase fallback notice:', err);
    }
  }

  // 3. Always save to local cache & notify Admin Dashboard listeners
  const current = getCachedInquiries() || [];
  const updated = [savedInquiry, ...current.filter((i) => i.id !== savedInquiry.id)];
  setCachedInquiries(updated);
  notifyInquiriesUpdated();

  return { success: true, data: savedInquiry };
}

export async function fetchInquiries(): Promise<Inquiry[]> {
  let fetchedList: Inquiry[] = [];
  let apiWorked = false;

  // 1. Try Express API
  try {
    const res = await fetch('/api/inquiries');
    if (res.ok) {
      const json = await res.json();
      if (Array.isArray(json.data)) {
        fetchedList = json.data;
        apiWorked = true;
      }
    }
  } catch (err) {
    console.warn('API inquiries fetch notice:', err);
  }

  // 2. Fallback to Supabase if API unreachable
  if (!apiWorked) {
    try {
      const { data } = await supabase
        .from('inquiries')
        .select('*')
        .order('created_at', { ascending: false });
      if (Array.isArray(data) && data.length > 0) {
        fetchedList = data as Inquiry[];
      }
    } catch {}
  }

  // 3. Merge with local cache so newly submitted inquiries are never lost
  const cached = getCachedInquiries() || [];
  const map = new Map<string, Inquiry>();

  // Add cached items first, then overlay with server items
  cached.forEach((item) => {
    if (item && item.id) map.set(item.id, item);
  });
  fetchedList.forEach((item) => {
    if (item && item.id) map.set(item.id, item);
  });

  const merged = Array.from(map.values()).sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
  );

  setCachedInquiries(merged);
  return merged;
}

export async function updateInquiryStatus(id: string, status: string): Promise<boolean> {
  // Update local cache immediately
  const cached = getCachedInquiries() || [];
  const updatedList = cached.map((item) => (item.id === id ? { ...item, status } : item));
  setCachedInquiries(updatedList);
  notifyInquiriesUpdated();

  try {
    const res = await fetch(`/api/inquiries/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (res.ok) return true;
  } catch (err) {
    console.warn('API update inquiry status notice:', err);
  }

  try {
    const { error } = await supabase.from('inquiries').update({ status }).eq('id', id);
    return !error;
  } catch {
    return true;
  }
}

export async function deleteInquiry(id: string): Promise<boolean> {
  // Update local cache immediately
  const cached = getCachedInquiries() || [];
  const updatedList = cached.filter((item) => item.id !== id);
  setCachedInquiries(updatedList);
  notifyInquiriesUpdated();

  try {
    const res = await fetch(`/api/inquiries/${id}`, { method: 'DELETE' });
    if (res.ok) return true;
  } catch (err) {
    console.warn('API delete inquiry notice:', err);
  }

  try {
    const { error } = await supabase.from('inquiries').delete().eq('id', id);
    return !error;
  } catch {
    return true;
  }
}

// 2. PROJECTS API
const LOCAL_PROJECTS_KEY = 'formacode_projects_cache';

const DEFAULT_PROJECTS: Project[] = [
  {
    id: 'proj-1',
    name: 'NovaBank Platform',
    link: 'https://novabank-example.com',
    description: 'FinTech Web Application with modern dashboard, real-time transactions, and high-security infrastructure.',
    category: 'Websites',
    tech: ['React', 'Next.js', 'PostgreSQL'],
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20clean%20dark%20fintech%20dashboard%20interface%20floating%20on%20a%20dark%20navy%20background%2C%20soft%20cyan%20and%20violet%20neon%20glow%2C%20sleek%20glassmorphism%20panels&width=900&height=600&seq=fc-tech-proj1&orientation=landscape',
    featured: true,
    created_at: '2026-01-15T10:00:00.000Z',
  },
  {
    id: 'proj-2',
    name: 'Atlas AI Agent',
    link: 'https://atlas-ai-example.com',
    description: 'AI Assistant & Automation Pipeline for enterprise support and workflow processing.',
    category: 'AI',
    tech: ['Python', 'OpenAI API', 'LangChain'],
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20clean%20AI%20assistant%20hologram%20with%20soft%20cyan%20neural%20network%20on%20a%20dark%20navy%20background&width=900&height=600&seq=fc-tech-proj2&orientation=landscape',
    featured: true,
    created_at: '2026-02-10T12:00:00.000Z',
  },
  {
    id: 'proj-3',
    name: 'Orbit Commerce',
    link: 'https://orbitcommerce-example.com',
    description: 'Next-gen headless e-commerce store with high performance checkout and custom theme.',
    category: 'E-commerce',
    tech: ['Shopify', 'React', 'Tailwind'],
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20clean%20e-commerce%20interface%20with%20floating%20product%20cards%20on%20a%20dark%20navy%20background&width=900&height=600&seq=fc-tech-proj3&orientation=landscape',
    featured: true,
    created_at: '2026-03-01T15:30:00.000Z',
  },
  {
    id: 'proj-4',
    name: 'CyberFlow AI Engine',
    link: 'https://cyberflow.ai',
    description: 'An advanced AI engine for workflow automation and data processing.',
    category: 'AI',
    tech: ['Python', 'PyTorch', 'FastAPI', 'React'],
    image: 'https://readdy.ai/api/search-image?query=3D%20rendered%20holographic%20abstract%20technology%20shape%20floating%20in%20dark%20purple%20space%20with%20glowing%20neon%20accents%2C%20premium%20technology%20aesthetic%2C%20clean%20minimal%20composition&width=1200&height=800&seq=fc-admin-new&orientation=landscape',
    featured: true,
    created_at: '2026-03-15T11:58:14.901Z',
  },
];

function getCachedProjects(): Project[] | null {
  try {
    const raw = localStorage.getItem(LOCAL_PROJECTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return null;
}

function setCachedProjects(projects: Project[]) {
  try {
    localStorage.setItem(LOCAL_PROJECTS_KEY, JSON.stringify(projects));
  } catch {}
}

function notifyProjectsUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('projects_updated'));
  }
}

export async function fetchProjects(): Promise<Project[]> {
  let fetchedList: Project[] = [];
  let apiWorked = false;

  // 1. Try Backend Express API
  try {
    const res = await fetch('/api/projects');
    if (res.ok) {
      const json = await res.json();
      if (Array.isArray(json.data)) {
        fetchedList = json.data;
        apiWorked = true;
      }
    }
  } catch (err) {
    console.warn('API projects fetch notice:', err);
  }

  // 2. Try Supabase directly if API failed
  if (!apiWorked) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && Array.isArray(data) && data.length > 0) {
        fetchedList = data as Project[];
      }
    } catch {
      // ignore
    }
  }

  // 3. Merge with Local Storage Cache so added projects are always present
  const cached = getCachedProjects() || DEFAULT_PROJECTS;
  const map = new Map<string, Project>();

  // Add cached items first
  cached.forEach((p) => {
    if (p && p.id) map.set(p.id, { ...p, link: normalizeProjectLink(p.link) });
  });

  // Overlay with server items
  fetchedList.forEach((p) => {
    if (p && p.id) map.set(p.id, { ...p, link: normalizeProjectLink(p.link) });
  });

  const merged = Array.from(map.values()).sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
  );

  setCachedProjects(merged);
  return merged;
}

export async function createProject(project: {
  name: string;
  link?: string;
  description?: string;
  category?: string;
  tech?: string[] | string;
  image?: string;
  featured?: boolean;
}): Promise<{ success: boolean; data?: Project; error?: string }> {
  const techArray = Array.isArray(project.tech)
    ? project.tech
    : typeof project.tech === 'string'
    ? project.tech.split(',').map((t) => t.trim()).filter(Boolean)
    : [];

  const normalizedLink = normalizeProjectLink(project.link);

  const newProj: Project = {
    id: 'proj_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7),
    name: project.name.trim(),
    link: normalizedLink || null,
    description: project.description?.trim() || null,
    category: project.category || 'Websites',
    tech: techArray,
    image:
      project.image?.trim() ||
      'https://readdy.ai/api/search-image?query=3D%20rendered%20holographic%20abstract%20technology%20shape%20floating%20in%20dark%20purple%20space%20with%20glowing%20neon%20accents%2C%20premium%20technology%20aesthetic%2C%20clean%20minimal%20composition&width=1200&height=800&seq=fc-admin-new&orientation=landscape',
    featured: Boolean(project.featured),
    created_at: new Date().toISOString(),
  };

  let savedData: Project = newProj;

  // 1. Try Backend Express API
  try {
    const res = await fetch('/api/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...project,
        link: normalizedLink,
        tech: techArray,
      }),
    });
    if (res.ok) {
      const json = await res.json();
      if (json.data) {
        savedData = {
          ...json.data,
          link: normalizeProjectLink(json.data.link),
        };
      }
    }
  } catch (err) {
    console.warn('API create project notice:', err);
  }

  // 2. Try Supabase Insert
  try {
    const { data, error } = await supabase
      .from('projects')
      .insert({
        id: savedData.id,
        name: savedData.name,
        link: savedData.link,
        description: savedData.description,
        category: savedData.category,
        tech: savedData.tech,
        image: savedData.image,
        featured: savedData.featured,
      })
      .select()
      .single();

    if (!error && data) {
      savedData = {
        ...(data as Project),
        link: normalizeProjectLink((data as Project).link),
      };
    }
  } catch (err) {
    console.warn('Supabase project insert notice:', err);
  }

  // 3. Always Sync with Local Storage Cache & Notify Components
  const current = getCachedProjects() || DEFAULT_PROJECTS;
  const updated = [savedData, ...current.filter((p) => p.id !== savedData.id)];
  setCachedProjects(updated);
  notifyProjectsUpdated();

  return { success: true, data: savedData };
}

export async function updateProject(
  id: string,
  updates: Partial<Project>
): Promise<{ success: boolean; data?: Project; error?: string }> {
  let updatedData: Project | null = null;
  const payload = {
    ...updates,
    link: updates.link !== undefined ? normalizeProjectLink(updates.link) : undefined,
  };

  // 1. Try Backend Express API
  try {
    const res = await fetch(`/api/projects/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const json = await res.json();
      if (json.data) {
        updatedData = {
          ...json.data,
          link: normalizeProjectLink(json.data.link),
        };
      }
    }
  } catch (err) {
    console.warn('API update project notice:', err);
  }

  // 2. Try Supabase Update
  try {
    const { data, error } = await supabase
      .from('projects')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (!error && data) {
      updatedData = {
        ...(data as Project),
        link: normalizeProjectLink((data as Project).link),
      };
    }
  } catch (err) {
    console.warn('Supabase project update notice:', err);
  }

  // 3. Always Sync with Local Storage Cache & Notify Components
  const current = getCachedProjects() || DEFAULT_PROJECTS;
  const list = current.map((item) => {
    if (item.id === id) {
      return updatedData || { ...item, ...updates };
    }
    return item;
  });
  setCachedProjects(list);
  notifyProjectsUpdated();

  const result = list.find((item) => item.id === id);
  return { success: true, data: result };
}

export async function deleteProject(id: string): Promise<boolean> {
  // 1. Try Backend Express API
  try {
    await fetch(`/api/projects/${id}`, { method: 'DELETE' });
  } catch (err) {
    console.warn('API delete project notice:', err);
  }

  // 2. Try Supabase Delete
  try {
    await supabase.from('projects').delete().eq('id', id);
  } catch (err) {
    console.warn('Supabase project delete notice:', err);
  }

  // 3. Always Sync with Local Storage Cache & Notify Components
  const current = getCachedProjects() || DEFAULT_PROJECTS;
  const list = current.filter((item) => item.id !== id);
  setCachedProjects(list);
  notifyProjectsUpdated();

  return true;
}
